import React from 'react'

import { Switch } from '@clientbase/clientbase-library'
import EmailIcon from '@mui/icons-material/Email'
import InfoIcon from '@mui/icons-material/Info'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { Box, Checkbox, Stack, Tooltip, Typography } from '@mui/material'

import { SENSITIVE_FIELD_LABELS } from '../../BodyModalNewChargeOrRecurrence.edit.utils'

export type NotifyChoice = 'silent' | 'notify'

export interface NotifyCustomerFieldsProps {
  changedFields: string[]
  notifyChoice: NotifyChoice
  onNotifyChoiceChange: (value: NotifyChoice) => void
  channels: { email: boolean; whatsapp: boolean }
  onChannelsChange: (channels: { email: boolean; whatsapp: boolean }) => void
  /** Tarifa ausente = canal sem cobrança (coexistence ativo no WhatsApp). */
  fees: { email?: number; whatsapp?: number }
  enabled: { email: boolean; whatsapp: boolean }
}

const formatMoney = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(value) || 0)

const ChannelRow: React.FC<{
  checked: boolean
  onToggle: () => void
  icon: React.ReactNode
  name: string
  fee?: number
  isFirst: boolean
}> = ({ checked, onToggle, icon, name, fee, isFirst }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{
      py: 1.25,
      px: 0.5,
      cursor: 'pointer',
      borderTop: isFirst ? 'none' : '1px solid',
      borderTopColor: 'divider'
    }}
    onClick={onToggle}
  >
    <Checkbox
      checked={checked}
      onChange={onToggle}
      onClick={(e) => e.stopPropagation()}
      size="small"
      sx={{ p: 0 }}
    />
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '8px',
        backgroundColor: '#F4F5F4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      {icon}
    </Box>
    <Typography variant="smBold" sx={{ flex: 1 }}>
      {name}
    </Typography>
    {fee != null && (
      <Typography variant="smLight" color="text.secondary">
        {formatMoney(fee)}
      </Typography>
    )}
  </Stack>
)

const NotifyCustomerFields: React.FC<NotifyCustomerFieldsProps> = ({
  changedFields,
  notifyChoice,
  onNotifyChoiceChange,
  channels,
  onChannelsChange,
  fees,
  enabled
}) => {
  const labels = changedFields
    .map((field) => SENSITIVE_FIELD_LABELS[field])
    .filter(Boolean)

  const isOn = notifyChoice === 'notify'

  return (
    <Stack direction="column" spacing={1.5} width="100%">
      {labels.length > 0 && (
        <Box
          sx={{
            fontSize: 13,
            py: 1,
            px: 1.5,
            backgroundColor: '#FAFBFA',
            borderRadius: '6px',
            borderLeft: '3px solid #B26A00',
            color: 'text.primary'
          }}
        >
          <Typography
            component="span"
            variant="smBold"
            sx={{ color: '#B26A00' }}
          >
            Você alterou:
          </Typography>{' '}
          <Typography component="span" variant="smLight">
            {labels.join(', ')}
          </Typography>
        </Box>
      )}

      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          py: 1.25,
          px: 1.5,
          backgroundColor: '#FAFBFA',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px'
        }}
      >
        <Box sx={{ flex: 1, pr: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="smBold" lineHeight={1.2}>
              Notificar novamente o cliente
            </Typography>
            <Tooltip
              arrow
              placement="top"
              title="Esta notificação extra será cobrada à parte, baseada nas condições e limites do seu plano."
            >
              <InfoIcon
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  display: 'block'
                }}
              />
            </Tooltip>
          </Stack>
          <Typography variant="smLight" color="text.secondary" display="block">
            Envia uma nova mensagem sobre a alteração
          </Typography>
        </Box>
        <Switch
          checked={isOn}
          onChange={(_, checked) =>
            onNotifyChoiceChange(checked ? 'notify' : 'silent')
          }
        />
      </Stack>

      {isOn && (enabled.email || enabled.whatsapp) && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            px: 1.5
          }}
        >
          {enabled.email && (
            <ChannelRow
              checked={channels.email}
              onToggle={() =>
                onChannelsChange({ ...channels, email: !channels.email })
              }
              icon={<EmailIcon sx={{ fontSize: 16, color: '#777777' }} />}
              name="E-mail"
              fee={fees.email}
              isFirst
            />
          )}
          {enabled.whatsapp && (
            <ChannelRow
              checked={channels.whatsapp}
              onToggle={() =>
                onChannelsChange({ ...channels, whatsapp: !channels.whatsapp })
              }
              icon={<WhatsAppIcon sx={{ fontSize: 16, color: '#777777' }} />}
              name="WhatsApp"
              fee={fees.whatsapp}
              isFirst={!enabled.email}
            />
          )}
        </Box>
      )}
    </Stack>
  )
}

export default NotifyCustomerFields
